import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const APPLIANCES = [
  { id: "cello-air-fryer-smart-pro", name: "Air Fryer Smart Pro 4.5L" },
  { id: "cello-electric-kettle", name: "Quick Boil Electric Kettle" },
  { id: "cello-otg-primo-40l", name: "OTG Primo+ 40L" },
  { id: "cello-air-fryer-smart", name: "Air Fryer Smart 4.5L" },
  { id: "cello-slow-juicer", name: "Slow Juicer" },
  { id: "cello-mixer-discovery-dlx", name: "Grind-N-Mix Discovery DLX" },
  { id: "cello-induction-blazing-600", name: "Blazing 600+ Induction" },
];

const COOKWARE = [
  { id: "cello-castello-4pcs-set", name: "Castello 4 Pcs Die-Cast Set" },
  { id: "cello-triply-imperiya-3pcs", name: "Tri-Ply Imperiya 3 Pcs Set" },
  { id: "cello-woody-4pcs-set", name: "Woody 4 Pcs Set" },
  { id: "cello-kitchenova-3pcs-set", name: "Kitchenova 3 Pcs Set" },
  { id: "cello-galaxy-steelox-set", name: "Galaxy Steelox Set" },
  { id: "cello-ironica-grill-pan", name: "Ironica Cast Iron Grill Pan" },
];

function CardGrid({ items, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
      {items.map((c, i) => (
        <Reveal key={c.id} delay={(i % 3) * 0.08}>
          <Link
            to={`/product/${c.id}`}
            data-testid={`${testidPrefix}-${c.id}`}
            className="group relative block overflow-hidden border border-white/10"
          >
            <img
              src={`/products/${c.id}-card.jpg`}
              alt={`Cello ${c.name}`}
              loading="lazy"
              className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)]"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#121212]/85 via-transparent to-transparent p-5 flex justify-end">
              <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-ivory bg-[#121212]/70 backdrop-blur-sm border border-white/15 px-3.5 py-2 group-hover:border-champagne group-hover:text-champagne transition-colors duration-300">
                View <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  );
}

export default function CelloCollection() {
  useEffect(() => {
    document.title = "CELLO Collection — THE GOOD GRID";
  }, []);

  return (
    <div data-testid="cello-collection-page" className="bg-charcoal text-ivory min-h-screen pt-28 lg:pt-36 pb-24">
      <div className="px-4 sm:px-8 lg:px-16">
        <Reveal><Eyebrow>CELLO × THE GOOD GRID</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-4 tracking-tight max-w-4xl leading-[1.05]">
            The art of the <span className="italic text-champagne-light">everyday gift.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-ivory/50 mt-6 max-w-xl leading-relaxed">
            Premium kitchen appliances, cookware and lifestyle products from Cello — practical, trusted and gift-ready. Curated for employee programmes, festive hampers and large-scale corporate orders.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <h2 className="font-serif text-2xl sm:text-3xl mt-16 mb-8 tracking-tight text-ivory/90">Kitchen Appliances</h2>
        </Reveal>
        <CardGrid items={APPLIANCES} testidPrefix="cello-card" />

        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl mt-20 mb-8 tracking-tight text-ivory/90">Cookware</h2>
        </Reveal>
        <CardGrid items={COOKWARE} testidPrefix="cello-card" />

        <div className="mt-20 border border-champagne/30 bg-[#151210] p-8 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal><Eyebrow>Festive & Bulk Programmes</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-5xl mt-4 tracking-tight">Festive bundle pricing, <span className="italic text-champagne-light">on request.</span></h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-ivory/50 mt-5 max-w-2xl text-sm leading-relaxed">
                Build Diwali, New Year and employee appreciation programmes around Cello appliances and cookware — mixed bundles, custom sleeves, message cards and multi-city dispatch. Share your quantity and budget per gift; we return a curated proposal.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.25}>
              <Link
                to="/request-quote"
                data-testid="cello-bundle-quote"
                className="group flex items-center justify-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] px-7 py-5 hover:bg-champagne-light transition-colors duration-300 w-full"
              >
                Request Bundle Quote <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <p className="text-ivory/30 text-xs mt-4 text-center">Response within one business day.</p>
            </Reveal>
          </div>
        </div>

        <p className="text-ivory/30 text-xs mt-10">Cello is a brand of BIC Cello (India) Pvt. Ltd. Products are sourced and curated by THE GOOD GRID for corporate gifting.</p>
      </div>
    </div>
  );
}
